import { Box, Skeleton, Stack, SxProps, Theme, TableCell } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCallback } from 'react';
import { ApolloError } from '@apollo/client';

import { Table, TableBody, TableHead, TableRow, LoadedContent } from '@occc-app/design-system';
import { ReimbursementsTimeSeriesDataPoint } from '@occc/graphql';
import { formatDateTime, DateTimeFormat, parseDate } from '@occc-app/lib/date';

import {
  SKELETON_HEIGHT,
  TABLE_CONTAINER_HEIGHT,
  TABLE_ROW_HEIGHT,
} from './scan-data-weekly-reimbursements-table.constants';

import { formatMoneyAmountWithCentsThreshold } from '../scan-data-overview.utils';

export type ScanDataWeeklyReimbursementsTableProps = {
  loading: boolean;
  error: ApolloError | undefined;
  data: ReimbursementsTimeSeriesDataPoint[] | undefined;
};

// TableCell has to be imported from @mui, otherwise circular dependency issue occurs and a huge number of tests are failing
const HeaderTableCell = styled(TableCell)(({ theme }) => ({
  // TableCell becomes grey when stickyHeader is enabled for Table
  backgroundColor: theme.palette.background.paper,
}));

const tableContainerSx: SxProps<Theme> = {
  height: TABLE_CONTAINER_HEIGHT,
  overflow: 'auto',
  border: (theme) => `1px solid ${theme.palette.divider}`,
  borderRadius: (theme) => theme.shape.borderRadius,
};

const tableRowSx: SxProps<Theme> = {
  height: TABLE_ROW_HEIGHT,
};

export const ScanDataWeeklyReimbursementsTable = ({
  loading,
  error,
  data,
}: ScanDataWeeklyReimbursementsTableProps) => {
  const renderWeeklyReimbursementsTable = useCallback(
    (weeklyReimbursementsTrends: ReimbursementsTimeSeriesDataPoint[]) => (
      <Box sx={tableContainerSx}>
        <Table stickyHeader size='small'>
          <TableHead>
            <TableRow sx={tableRowSx}>
              <HeaderTableCell>Week End Date</HeaderTableCell>
              <HeaderTableCell align='right'>Multipack Total</HeaderTableCell>
              <HeaderTableCell align='right'>Loyalty Total</HeaderTableCell>
              <HeaderTableCell align='right'>Total</HeaderTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {weeklyReimbursementsTrends.map((row) => {
              const multipackTotal = row.value.multiUnit;
              const loyaltyTotal = row.value.loyalty;
              const total = multipackTotal + loyaltyTotal;
              const date = parseDate(row.date);

              return (
                <TableRow key={row.date} sx={tableRowSx}>
                  <TableCell>{formatDateTime(date, DateTimeFormat.DateDashed)}</TableCell>
                  <TableCell align='right'>
                    {formatMoneyAmountWithCentsThreshold(multipackTotal)}
                  </TableCell>
                  <TableCell align='right'>
                    {formatMoneyAmountWithCentsThreshold(loyaltyTotal)}
                  </TableCell>
                  <TableCell align='right'>{formatMoneyAmountWithCentsThreshold(total)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    ),
    [],
  );

  const renderLoadingState = useCallback(
    () => (
      <Stack height={TABLE_CONTAINER_HEIGHT} spacing={1}>
        {[...Array(5)].map((_, index) => (
          <Skeleton key={index} variant='rectangular' width='100%' height={SKELETON_HEIGHT} />
        ))}
      </Stack>
    ),
    [],
  );

  return (
    <LoadedContent
      loading={loading}
      error={error}
      items={data}
      renderData={renderWeeklyReimbursementsTable}
      renderLoading={renderLoadingState}
    />
  );
};
